import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Linkedin, ExternalLink } from "lucide-react";
import { NAV, CONTACT, IMAGES, FOUNDER, VERIFY } from "../data/content";

export const CrisisNote = ({ variant = "default" }) => (
  <div
    data-testid="crisis-note"
    className={`rounded-2xl border border-navy/10 p-6 md:p-8 ${
      variant === "sage" ? "bg-sage/20" : "bg-white"
    }`}
  >
    <p className="label text-navy/50 mb-3">In Crisis?</p>
    <p className="text-navy-deep/80 leading-relaxed text-sm md:text-base">
      If you or someone you know is in crisis, please call or text{" "}
      <a href="tel:988" className="font-semibold text-navy underline underline-offset-2">988</a>{" "}
      (Suicide &amp; Crisis Lifeline) or visit{" "}
      <a href="https://988lifeline.org" target="_blank" rel="noreferrer" className="font-semibold text-navy underline underline-offset-2">988lifeline.org</a>.
      For veteran-specific support, contact the <strong>Veterans Crisis Line</strong> at 988, then press&nbsp;1.
    </p>
  </div>
);

const EXTRA_LINKS = [
  { to: "/behavioral-health-education-for-veterans-and-families", label: "What is behavioral-health education?", id: "category" },
  { to: "/vs/diy-journaling-and-worksheets", label: "vs DIY journaling and worksheets", id: "vs-diy" },
  { to: "/vs/the-va-pathway", label: "How we fit beside the VA", id: "vs-va" },
  { to: "/vs/psycharmor", label: "vs PsychArmor", id: "vs-psycharmor" },
  { to: "/vs/va-ptsd-coach", label: "vs VA PTSD Coach", id: "vs-ptsd-coach" },
  { to: "/faq", label: "FAQ", id: "faq" },
  { to: "/verify", label: "Verify a certificate", id: "verify" },
  { to: "/survey", label: "Consultation Feedback", id: "consultation-feedback" },
];

export const Footer = () => (
  <footer className="relative z-10 bg-navy-deep text-cream/80" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-14 md:grid-cols-12">
      <div className="md:col-span-5">
        <div className="flex items-center gap-3 mb-6">
          <img src={IMAGES.tree} alt="Officium Mentale tree emblem" className="w-12 h-12 object-contain shrink-0 brightness-0 invert opacity-90" />
          <span className="font-serif text-cream text-xl">Officium Mentale LLC</span>
        </div>
        <p className="font-serif text-2xl md:text-3xl text-cream leading-snug max-w-md">
          Healing Minds. Serving Those Who Served.
        </p>
        <p className="mt-6 text-sm text-cream/50 max-w-sm leading-relaxed">
          Veteran-owned behavioral-health education and program management — structured questioning that helps people bring better questions to qualified providers. Serving Texas communities and beyond.
        </p>
      </div>

      <div className="md:col-span-3">
        <p className="label text-gold mb-5">Navigate</p>
        <ul className="space-y-3">
          <li>
            <Link to="/" className="text-cream/70 hover:text-gold transition-colors text-sm" data-testid="footer-link-home">Home</Link>
          </li>
          {NAV.map((n) => (
            <li key={n.path}>
              <Link to={n.path} className="text-cream/70 hover:text-gold transition-colors text-sm" data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}{n.hint && <span className="text-cream/40"> · {n.hint.toLowerCase()}</span>}
              </Link>
            </li>
          ))}
          {EXTRA_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="text-cream/70 hover:text-gold transition-colors text-sm" data-testid={`footer-link-${l.id}`}>{l.label}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-4">
        <p className="label text-gold mb-5">Contact</p>
        <ul className="space-y-4 text-sm">
          <li className="flex gap-3"><Phone size={18} className="text-sage shrink-0" /><a href={`tel:${CONTACT.phone}`} className="hover:text-gold transition-colors">{CONTACT.phone}</a></li>
          <li className="flex gap-3"><Mail size={18} className="text-sage shrink-0" /><a href={`mailto:${CONTACT.email}`} className="hover:text-gold transition-colors break-all">{CONTACT.email}</a></li>
          <li className="flex gap-3"><Globe size={18} className="text-sage shrink-0" /><span>{CONTACT.website}</span></li>
          {FOUNDER.linkedin && (
            <li className="flex gap-3"><Linkedin size={18} className="text-sage shrink-0" /><a href={FOUNDER.linkedin} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="footer-linkedin">{FOUNDER.name} on LinkedIn</a></li>
          )}
        </ul>
        <p className="label text-gold mt-8 mb-4">Verify us</p>
        <ul className="space-y-3 text-sm">
          <li><a href={VERIFY.dsbs} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-cream/70 hover:text-gold transition-colors" data-testid="footer-verify-vosb">VOSB certification · SBA DSBS <ExternalLink size={12} /></a></li>
          <li><a href={VERIFY.sam} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-cream/70 hover:text-gold transition-colors" data-testid="footer-verify-sam">SAM.gov entity record · UEI QH9YT9CBCKX8 <ExternalLink size={12} /></a></li>
          {FOUNDER.linkedin && (
            <li><a href={FOUNDER.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-cream/70 hover:text-gold transition-colors" data-testid="footer-verify-linkedin"><Linkedin size={12} /> {FOUNDER.name}, founder · LinkedIn <ExternalLink size={12} /></a></li>
          )}
        </ul>
      </div>
    </div>

    <div className="border-t border-cream/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row gap-3 justify-between text-xs text-cream/40">
        <p>© {new Date().getFullYear()} Officium Mentale LLC · Veteran-Owned Small Business (VOSB), SBA certified · Founded December 2025, Texas</p>
        <p>Not a clinical practice. Behavioral-health education, support &amp; program management only.</p>
      </div>
    </div>
  </footer>
);
